<script setup>

import {computed, ref} from "vue";
import {useSettingsStore} from "@/store/settings";
import AdminUsers from "@/components/AdminView/AdminUsers.vue";
import AdminNews from "@/components/AdminView/AdminNews.vue";
import AdminProducts from "@/components/AdminView/AdminProducts.vue";
import AdminCustomPages from "@/components/AdminView/AdminCustomPages.vue";
import AdminOrders from "@/components/AdminView/AdminOrders.vue";
import AdminPriceList from "@/components/AdminView/AdminPriceList.vue";
import AdminCoins from "@/components/AdminView/AdminCoins.vue";
import AdminMailings from "@/components/AdminView/AdminMailings.vue";
import AdminRoles from "@/components/AdminView/AdminRoles.vue";
import AdminSystemStatus from "@/components/AdminView/AdminSystemStatus.vue";

const settingsStore = useSettingsStore();
const isSuperadmin = computed(() => settingsStore.hasRole('superadmin'));

const baseSections = [
    {key: 'admin-custom-pages', label: 'Страницы'},
    {key: 'admin-orders', label: 'Заказы'},
    {key: 'admin-users', label: 'Пользователи'},
    {key: 'admin-roles', label: 'Роли'},
    {key: 'admin-news', label: 'Новости'},
    {key: 'admin-mailings', label: 'Рассылки'},
    {key: 'admin-products', label: 'Товары'},
    {key: 'admin-price-list', label: 'Прайс-лист'},
    {key: 'admin-coins', label: 'Монеты'},
];

const sections = computed(() => {
    if (!isSuperadmin.value) {
        return baseSections;
    }
    const list = [...baseSections];
    const index = list.findIndex(section => section.key === 'admin-custom-pages');
    list.splice(index + 1, 0, {key: 'admin-system', label: 'Состояние системы'});
    return list;
});

const activeSection = ref('admin-custom-pages');

</script>

<template>
  <div class="adminView">
    <aside class="adminView__navigator">
      <div
          v-for="section in sections"
          :key="section.key"
          class="adminView__item"
          :class="{ 'adminView__item--active': activeSection === section.key }"
          @click="activeSection = section.key"
      >
        <span class="adminView__itemLabel">{{ section.label }}</span>
        <span class="adminView__itemKey">{{ section.key }}</span>
      </div>
    </aside>
    <section class="adminView__detail">
      <AdminUsers v-if="activeSection === 'admin-users'"/>
      <AdminRoles v-else-if="activeSection === 'admin-roles'"/>
      <AdminNews v-else-if="activeSection === 'admin-news'"/>
      <AdminMailings v-else-if="activeSection === 'admin-mailings'"/>
      <AdminProducts v-else-if="activeSection === 'admin-products'"/>
      <AdminCustomPages v-else-if="activeSection === 'admin-custom-pages'"/>
      <AdminOrders v-else-if="activeSection === 'admin-orders'"/>
      <AdminPriceList v-else-if="activeSection === 'admin-price-list'"/>
      <AdminCoins v-else-if="activeSection === 'admin-coins'"/>
      <AdminSystemStatus v-else-if="activeSection === 'admin-system' && isSuperadmin"/>
    </section>
  </div>
</template>

<style scoped lang="scss">
.adminView {
  display: grid;
  grid-template-columns: 282px 1fr;
  column-gap: 24px;
  align-items: start;
  text-align: left;

  @include respond-to('md') {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__navigator {
    min-height: 400px;
    border-radius: 20px;
    gap: 16px;
    padding: 24px;
    background-color: var(--cd-card-bg);
    display: flex;
    flex-direction: column;

    @include respond-to('md') { min-height: auto; }
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px 16px;
    border-radius: 12px;
    cursor: pointer;
    color: var(--cd-text-muted);
    border: 1px solid transparent;
    transition: all 0.3s;

    &:hover {
      color: var(--cd-highlight-text);
    }

    &--active {
      background: var(--cd-button-bg-color);
      border-color: var(--cd-base-green);
      color: var(--cd-text);
    }
  }

  &__itemKey {
    font-size: 12px;
    opacity: 0.7;
  }

  &__detail {
    min-width: 0;
  }
}
</style>
